import { useRef, useState } from "react";
import {
  Box,
  IconButton,
  Radio,
  Checkbox,
  Button,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Add, Delete, Image as ImageIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";
import AnswerOptionRichEditor from "./AnswerOptionRichEditor";
import HtmlWithLatex from "./HtmlWithLatex";

const hasMath = (text) => /\\\(|\\\[|\\frac|\\sqrt|\\times|\\div|\$[^$]+\$/.test(text || "");

const hasInlineImage = (html) => /<img\b/i.test(String(html || ""));

// An option image can live in image_url (uploaded) or inside the option HTML (pasted into the
// rich editor), so removing "the image" has to clear both or the picture stays on screen.
const stripInlineImages = (html) => {
  const stripped = String(html || "").replace(/<img\b[^>]*>/gi, "");
  return stripped.replace(/<[^>]*>/g, "").trim() ? stripped : "";
};

const McqOptions = ({
  options,
  setOptions,
  correctIndex,
  setCorrectIndex,
  correctIndices,
  setCorrectIndices,
  isMultiSelect = false,
}) => {
  const fileRef = useRef(null);
  const pickIdxRef = useRef(null);
  const [busyIdx, setBusyIdx] = useState(null);

  const handleOptionChange = (index, value) => {
    const updated = [...options];
    updated[index] = { ...updated[index], text: value };
    setOptions(updated);
  };

  const setOptionImage = (index, imageUrl) => {
    const updated = [...options];
    const current = updated[index] || {};
    // Picking/removing an image also drops a pasted inline image, otherwise the option ends up
    // showing two pictures (or the old one after "Remove image").
    updated[index] = {
      ...current,
      image_url: imageUrl || "",
      text: hasInlineImage(current.text) ? stripInlineImages(current.text) : current.text,
    };
    setOptions(updated);
  };

  const triggerPick = (index) => {
    pickIdxRef.current = index;
    fileRef.current?.click();
  };

  const handleImageFile = async (e) => {
    const idx = pickIdxRef.current;
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (idx == null || !file) return;
    setBusyIdx(idx);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post("/admin/question-bank/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.data?.image_url || "";
      if (url) {
        setOptionImage(idx, url);
        toast.success("Option image uploaded");
      }
    } catch {
      toast.error("Image upload failed");
    } finally {
      setBusyIdx(null);
      pickIdxRef.current = null;
    }
  };

  const addOption = () => {
    setOptions([...options, { text: "", image_url: "" }]);
  };

  const removeOption = (index) => {
    // Deleting is how a wrong option gets cleared, so it must work at the two-option minimum too:
    // top the list back up with blank rows instead of blocking the delete.
    const updated = options.filter((_, i) => i !== index);
    while (updated.length < 2) updated.push({ text: "", image_url: "" });
    setOptions(updated);

    if (isMultiSelect) {
      setCorrectIndices(
        correctIndices
          .filter((i) => i !== index)
          .map((i) => (i > index ? i - 1 : i))
      );
    } else if (correctIndex === index) {
      setCorrectIndex(0);
    } else if (correctIndex > index) {
      setCorrectIndex(correctIndex - 1);
    }
  };

  const toggleCorrectMulti = (index) => {
    if (correctIndices.includes(index)) {
      setCorrectIndices(correctIndices.filter((i) => i !== index));
    } else {
      setCorrectIndices([...correctIndices, index]);
    }
  };

  return (
    <Box>
      <input
        type="file"
        accept="image/png, image/jpeg, image/webp, image/gif"
        ref={fileRef}
        style={{ display: "none" }}
        onChange={handleImageFile}
      />
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#444" }}>
        Options {isMultiSelect ? "(select all correct)" : "(select correct)"}
      </Typography>
      {options.map((opt, index) => {
        // Generated table/diagram options carry no text at all — a 44px thumbnail is unusable
        // for comparing them, so an image-only option gets a full-size preview instead.
        const imageOnly =
          Boolean(opt.image_url) && !String(opt.text || "").replace(/<[^>]*>/g, "").trim();
        const optHasImage = Boolean(opt.image_url) || hasInlineImage(opt.text);

        return (
        <Paper
          key={index}
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
            p: 1,
            border: "1px solid",
            borderColor: isMultiSelect
              ? correctIndices.includes(index)
                ? "#4caf50"
                : "#e0e0e0"
              : correctIndex === index
                ? "#6366f1"
                : "#e0e0e0",
            borderRadius: 1,
            background:
              (isMultiSelect && correctIndices.includes(index))
                ? "rgba(76, 175, 80, 0.04)"
                : !isMultiSelect && correctIndex === index
                  ? "rgba(99, 102, 241, 0.04)"
                  : "#fafafa",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#6366f1",
              background: "rgba(99, 102, 241, 0.02)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {isMultiSelect ? (
              <Checkbox
                checked={correctIndices.includes(index)}
                onChange={() => toggleCorrectMulti(index)}
                sx={{
                  color: "#999",
                  "&.Mui-checked": { color: "#4caf50" },
                }}
              />
            ) : (
              <Radio
                checked={correctIndex === index}
                onChange={() => setCorrectIndex(index)}
                sx={{
                  color: "#999",
                  "&.Mui-checked": { color: "#6366f1" },
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: "1 1 200px", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {imageOnly && (
              <Box
                component="img"
                src={opt.image_url}
                alt=""
                sx={{
                  maxHeight: 220,
                  maxWidth: "100%",
                  alignSelf: "flex-start",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  objectFit: "contain",
                  bgcolor: "#fff",
                }}
              />
            )}
            <AnswerOptionRichEditor
              value={opt.text ?? ""}
              onChange={(html) => handleOptionChange(index, html)}
              placeholder={`Option ${index + 1}`}
            />
            {hasMath(opt.text) && (
              <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: "rgba(99,102,241,0.06)" }}>
                <HtmlWithLatex html={opt.text} />
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={busyIdx === index ? <CircularProgress size={14} /> : <ImageIcon />}
                onClick={() => triggerPick(index)}
                disabled={busyIdx !== null}
                sx={{ textTransform: "none", py: 0.25, fontSize: "0.75rem" }}
              >
                {optHasImage ? "Change image" : "Add image"}
              </Button>
              {optHasImage ? (
                <>
                  {opt.image_url && !imageOnly && (
                    <Box
                      component="img"
                      src={opt.image_url}
                      alt=""
                      sx={{ maxHeight: 44, maxWidth: 120, borderRadius: 1, border: "1px solid #e0e0e0", objectFit: "contain" }}
                    />
                  )}
                  <Button size="small" color="error" onClick={() => setOptionImage(index, "")} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    Remove image
                  </Button>
                </>
              ) : null}
            </Box>
          </Box>
          <IconButton
            onClick={() => removeOption(index)}
            color="error"
            size="small"
            sx={{ alignSelf: "flex-start" }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Paper>
        );
      })}
      <Button
        startIcon={<Add />}
        size="small"
        onClick={addOption}
        disabled={false}
        sx={{
          color: "#6366f1",
          "&:hover": { background: "rgba(99, 102, 241, 0.08)" },
          "&.Mui-disabled": { color: "#ccc" },
        }}
      >
        Add Option
      </Button>
    </Box>
  );
};

export default McqOptions;
